import { useEffect, useState } from "react";

import { AnalyticsEvents, AnalyticsService } from "utils/analytics";

export interface AudioDevice {
  deviceId: string;
  label: string;
}

export const useAudioDevices = () => {
  const [audioInputs, setAudioInputs] = useState<AudioDevice[]>([]);
  const [audioOutputs, setAudioOutputs] = useState<AudioDevice[]>([]);
  const [selectedInputId, setSelectedInputId] = useState<string>("");
  const [selectedOutputId, setSelectedOutputId] = useState<string>("");

  useEffect(() => {
    const getDevices = async () => {
      try {
        // Check if setSinkId is supported (not available in Safari)
        const supportsAudioOutput = "setSinkId" in HTMLMediaElement.prototype;

        const devices = await navigator.mediaDevices.enumerateDevices();

        const inputs = devices
          .filter(device => device.kind === "audioinput")
          .map(device => ({
            deviceId: device.deviceId,
            label: device.label || `Microphone ${device.deviceId.slice(0, 5)}`,
          }));

        // Only enumerate audio outputs if browser supports it
        const outputs = supportsAudioOutput
          ? devices
              .filter(device => device.kind === "audiooutput")
              .map(device => ({
                deviceId: device.deviceId,
                label: device.label || `Speaker ${device.deviceId.slice(0, 5)}`,
              }))
          : [];

        setAudioInputs(inputs);
        setAudioOutputs(outputs);

        // Set default devices if not already set
        if (!selectedInputId && inputs.length > 0) {
          setSelectedInputId(inputs[0].deviceId);
        }
        if (!selectedOutputId && outputs.length > 0) {
          setSelectedOutputId(outputs[0].deviceId);
        }
      } catch (error) {
        AnalyticsService.track(AnalyticsEvents.VIDEO_CALL.AUDIO_DEVICE.ENUMERATE_ERROR, {
          error: String(error),
        });
      }
    };

    getDevices();

    // Listen for device changes
    navigator.mediaDevices.addEventListener("devicechange", getDevices);

    return () => {
      navigator.mediaDevices.removeEventListener("devicechange", getDevices);
    };
  }, [selectedInputId, selectedOutputId]);

  return {
    audioInputs,
    audioOutputs,
    selectedInputId,
    selectedOutputId,
    setSelectedInputId,
    setSelectedOutputId,
  };
};
